import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import KnobHero from '../components/KnobHero';
import SectionHead from '../components/SectionHead';
import Reveal from '../components/Reveal';
import { BTN_PRIMARY, BTN_SECONDARY, LINK_ARROW, EYEBROW } from '../components/styles';
import { PRODUCTS, PRODUCT_BUCKETS } from '../content/products';
import { INDUSTRIES } from '../content/industries';
import usePageMeta from '../hooks/usePageMeta';

const STEPS = [
  {
    n: '01',
    t: 'Discuss requirements',
    d: 'Tell us about your buildings, appliances and the outcomes you desire.',
  },
  {
    n: '02',
    t: 'Assess compatibility',
    d: 'We review your appliance models to determine the RangeSafe Stove Safety Knob™ that will best address your concern.',
  },
  {
    n: '03',
    t: 'Evaluate a pilot',
    d: 'Conduct a pre-purchase pilot program to confirm fit, operation and testing feedback.',
  },
  {
    n: '04',
    t: 'Plan purchasing',
    d: 'Post-pilot program, we will help create a business case including models, quantities and pricing for management or Board review.',
  },
];

export default function Home() {
  usePageMeta(
    'Stove Safety Knobs for Commercial Properties | RangeSafe',
    'RangeSafe stove safety knobs help property owners and managers reduce accidental stove actuation — protecting tenants, limiting liability and aiding compliance.'
  );
  return (
    <main>
      <KnobHero />

      {/* SECTION 2 — OUR PRODUCTS (product overview + mechanism) */}
      <section className="mx-auto max-w-shell px-5 py-24 sm:px-8 lg:py-36" data-testid="section-our-products">
        <SectionHead
          eyebrow="Our Products"
          lines={['RangeSafe Stove Safety Knobs™']}
          lead="RangeSafe Stove Safety Knobs™ are innovative products that serve as both an attractive addition to stoves as well as an effective safety measure. The key to the RangeSafe Stove Safety Knob™ is the patented “Push, Press and Turn™” mechanism that reduces the risk of unintentional actuation of gas and electric stoves that could result in fires and gas leaks."
        />
        <div className="mt-16 grid items-center gap-12 lg:grid-cols-2">
          <Reveal className="order-2 lg:order-1">
            <ol className="space-y-0 border-t border-line">
              {[
                ['Push', 'Both sides of the knob must be intentionally and simultaneously pushed to unlock the knob.'],
                ['Press', 'Press the knob inward, toward the stove.'],
                ['Turn', 'Turn to the desired setting. The knob re-locks when returned to the off position.'],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-6 border-b border-line py-7">
                  <span className="font-mono text-[11px] tracking-[0.3em] text-accentText">0{i + 1}</span>
                  <div>
                    <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white">{t}</h3>
                    <p className="mt-2 max-w-sm text-base leading-relaxed text-dim">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-6 max-w-md text-xs leading-relaxed text-mute">
              RangeSafe Classic, Compact and Deluxe models require the “Push, Press and Turn™” steps. The Ultimate knob, by design, has a fourth unlocking step for added safety for children and adults with cognitive disabilities.
            </p>
            <Link to="/products" className={`${BTN_SECONDARY} mt-8`} data-testid="our-products-how-cta">
              Our Products
            </Link>
          </Reveal>
          <Reveal delay={0.12} className="order-1 lg:order-2">
            <figure className="relative border border-line" data-testid="our-products-still">
              <img
                src="/assets/home/knob-turn-still.jpg"
                alt="A hand operating a RangeSafe stove safety knob: pressing the knob inward toward the stove before turning it from the off position."
                className="block h-auto w-full"
                width="1536"
                height="1024"
                loading="lazy"
                data-testid="our-products-still-image"
              />
              <figcaption className="border-t border-line px-6 py-4 font-mono text-[9px] uppercase tracking-[0.2em] text-mute sm:text-[10px]">
                RangeSafe knob in operation — Push, Press and Turn™
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* SECTION 3 — FIND YOUR APPLICATION */}
      <section className="border-t border-line bg-panel/40 py-24 lg:py-36" data-testid="section-applications">
        <div className="mx-auto max-w-shell px-5 sm:px-8">
          <SectionHead
            eyebrow="Our Customers"
            lines={['Solutions for', 'Your Industry']}
            lead="RangeSafe’s products are appropriate for organizations that own or manage multiple dwellings where stove safety is an important requirement."
          />
          <div className="mt-16 space-y-14 lg:space-y-20">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.slug} delay={0.05}>
                <Link
                  to={`/industries/${ind.slug}`}
                  className="group grid items-center gap-8 md:grid-cols-12"
                  data-testid={`application-row-${ind.slug}`}
                >
                  <div className={`md:col-span-7 ${i % 2 === 1 ? 'md:order-2' : ''}`}>
                    <div className="overflow-hidden border border-line">
                      <img
                        src={ind.image}
                        alt={ind.alt}
                        className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        width="1600"
                        height="900"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-5">
                    <p className="font-mono text-[11px] tracking-[0.3em] text-accentText">0{i + 1}</p>
                    <h3 className="mt-3 font-display text-3xl font-bold uppercase leading-none tracking-tight text-white sm:text-4xl">
                      {ind.short}
                    </h3>
                    <p className="mt-4 max-w-md text-sm leading-relaxed text-dim sm:text-base">
                      {ind.lead}
                    </p>
                    <span className={`${LINK_ARROW} mt-6`}>
                      Explore <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — EXPLORE PRODUCTS */}
      <section className="border-t border-line bg-panel/40 py-24 lg:py-36" data-testid="section-products">
        <div className="mx-auto max-w-shell px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHead
              eyebrow="Our Product Line"
              lines={['Explore RangeSafe Products']}
              lead="RangeSafe provides a collection of stove safety products designed to meet various preferences and stove specifications."
            />
            <Reveal delay={0.1}>
              <Link to="/products#compare" className={BTN_SECONDARY} data-testid="compare-products-cta">
                Compare Products
              </Link>
            </Reveal>
          </div>
          <div className="mt-16 grid gap-px border border-line bg-line lg:grid-cols-3">
            {PRODUCT_BUCKETS.map((bucket, i) => {
              const items = PRODUCTS.filter((p) => p.bucket === bucket.name);
              return (
                <Reveal key={bucket.name} delay={i * 0.06} className="bg-base">
                  <Link to="/products" className="group flex h-full flex-col p-8 transition-colors hover:bg-panel" data-testid={`line-bucket-${bucket.name.toLowerCase()}`}>
                    <div className="spec-frame relative flex h-60 items-center justify-center overflow-hidden border border-line2">
                      <img
                        src={bucket.repImage}
                        alt={`RangeSafe ${bucket.name} bucket — representative stove safety knob`}
                        className="bucket-knob h-44 w-auto object-contain drop-shadow-[0_18px_20px_rgba(0,0,0,0.45)] transition-opacity duration-500 ease-out group-hover:opacity-0"
                        width="768"
                        height="1024"
                        loading="lazy"
                      />
                      {bucket.hoverImage && (
                        <img
                          src={bucket.hoverImage}
                          alt={`RangeSafe ${bucket.name} knobs installed on a stove`}
                          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100"
                          loading="lazy"
                          aria-hidden="true"
                          data-testid={`line-bucket-${bucket.name.toLowerCase()}-stove`}
                        />
                      )}
                      <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(55%_38%_at_50%_0%,rgba(229,231,235,0.05),transparent_70%)]" aria-hidden="true" />
                    </div>
                    <p className="mt-7 font-mono text-[9px] uppercase tracking-[0.3em] text-accentText">Line {String(i + 1).padStart(2, '0')}</p>
                    <h3 className="mt-3 font-display text-2xl font-bold uppercase leading-tight tracking-tight text-white">
                      {bucket.name}
                    </h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-dim">{bucket.blurb}</p>
                    <p className="mt-6 border-t border-line pt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-mute">
                      {items.length} models
                    </p>
                    <span className={`${LINK_ARROW} mt-4`}>
                      Explore the Line <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 6 — EVALUATE A COMMERCIAL PURCHASE */}
      <section className="border-t border-line py-24 lg:py-36" data-testid="section-journey">
        <div className="mx-auto max-w-shell px-5 sm:px-8">
          <SectionHead
            eyebrow="Commercial Purchasing"
            lines={['Support for', 'commercial decision making']}
            lead="RangeSafe Commercial Solutions offers a no cost, no obligation initial consultation to help prospective commercial clients assess which RangeSafe product is best suited for their needs and help develop pre-purchase pilot programs."
          />
          <div className="mt-16 grid gap-px border border-line bg-line md:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.06} className="bg-base">
                <div className="h-full p-7">
                  <p className="font-mono text-[11px] tracking-[0.3em] text-accentText">{s.n}</p>
                  <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white">{s.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-dim">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to="/quote?need=pilot" className={`${BTN_PRIMARY} mt-8`} data-testid="discuss-pilot-cta">
              Request a Consultation
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SECTION 7 — MANUFACTURER INQUIRIES */}
      <section className="relative overflow-hidden border-t border-line" data-testid="section-oem">
        <img
          src="/assets/hero/scene-knob-row.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
          width="1264"
          height="848"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-base via-base/80 to-base/40" aria-hidden="true" />
        <div className="relative mx-auto max-w-shell px-5 py-24 sm:px-8 lg:py-36">
          <SectionHead
            eyebrow="Manufacturers & OEM Teams"
            lines={['Purchasing and Licensing Options']}
            lead="Build RangeSafe’s patented stove safety technology into your next appliance platform. Partner with us to source knobs for factory installation or explore licensing the technology for integration into your own designs."
          />
          <Reveal delay={0.1}>
            <Link to="/manufacturers" className={`${BTN_PRIMARY} mt-10`} data-testid="oem-cta">
              Discuss an OEM Opportunity
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
